import api from "@/lib/api";
import { API_URL } from "@/lib/constants";
import { githubAppApi } from "@/lib/githubApp/api";
import { useMutation, useQuery } from "@tanstack/react-query";

type StartInstallVars = { userId: string };

type StartInstallResp = {
  // Your backend can return just installation_url
  installation_url: string;
  // if you also return state, keep it for debugging/analytics
  state?: string;
};

function openPopup(url: string): Window | null {
  // Graceful fallback when popups are blocked
  const w = window.open(
    url,
    "github-app-install",
    "width=960,height=800,noopener,noreferrer,scrollbars=yes",
  );
  // if (!w) {
  //   // fallback: navigate current tab
  //   window.location.href = url;
  // }
  return w;
}

export function useGitHubAppInstall() {
  return useMutation({
    mutationFn: async ({
      userId,
    }: StartInstallVars): Promise<StartInstallResp> => {
      const res = await api.post(`${API_URL}/v1/github/install`, {
        firebaseUid: userId,
      });
      return res.data;
    },
    onSuccess: ({ installation_url, state }) => {
      const popup = openPopup(`${installation_url}?state=${state}`);

      // Optional: safety net — if popup gets closed without posting a message,
      // trigger a soft refresh after a short delay.
      const tick = window.setInterval(() => {
        if (popup && popup.closed) {
          window.clearInterval(tick);
          // Let your UI try a refresh — user might have completed install elsewhere
          window.dispatchEvent(
            new MessageEvent("message", { data: "github-app-install-success" }),
          );
        }
      }, 1000);
    },
  });
}

export const useGetGitHubInstallations = (
  enabled: boolean,
  refresh?: boolean,
) => {
  return useQuery({
    queryKey: ["github-installations"],
    queryFn: githubAppApi.getInstallations,
    refetchInterval: refresh ? 1000 : false,
    enabled,
  });
};

export const useGetGitHubBranches = (
  repoName: string,
  installationId: number | null,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["github-branches", repoName, installationId],
    queryFn: () => {
      if (!installationId) {
        throw new Error("Installation ID is required");
      }
      return githubAppApi.getBranches(repoName, installationId);
    },
    select: (data) => data?.items ?? [],
    enabled: enabled && !!installationId,
  });
};
